import { TrackBuilder, type BuiltPoint, type PieceOptions } from './builder';

/**
 * 実在のコースターを真似たサンプルコース（形・数値は、公開されている概要（高さ・最高速度・全長・
 * 発進の加速など）からの近似。実物の図面ではない）。
 * どれも駅を出発し、最後の停止ブレーキで止まる。車両の限界値（上下 G +5 / −1.5、横 G 1.8）に
 * 収まるように、縦の曲線は上下 G を指定して作る（{@link TrackBuilder.pitchByG}）。
 * 走行・評価は `project/sample.test.ts` で確かめる。
 */

const STANDARD_GRAVITY = 9.80665;

/** 発進加速の区間（目標速度 [m/s]・加速度 [m/s²]）。 */
function launch(targetSpeed: number, maxAcceleration: number): PieceOptions {
  return { segment: 'launch', params: { targetSpeed, maxAcceleration } };
}

/** ブレーキの減速度 [m/s²]（磁気ブレーキ。約 0.6 G）。 */
const BRAKE_DECELERATION = 6;

/** 頂上を越える縦の曲線の上下 G（強く浮く。車両の下限 −1.5 G の手前）。 */
const CREST_G = -1.1;

/** 引き起こし（谷）の上下 G（車両の上限 5 G の手前）。 */
const PULL_G = 4.3;

/**
 * 高さ `height` での速度の見積もり [m/s]：高さ `fromHeight` で速度 `fromSpeed` から、力学的エネルギー保存
 * （抵抗の分として `keep` を掛ける）。
 */
function speedAt(fromSpeed: number, fromHeight: number, height: number, keep = 0.97): number {
  const v2 = fromSpeed * fromSpeed - 2 * STANDARD_GRAVITY * (height - fromHeight);
  return Math.sqrt(Math.max(1, v2)) * keep;
}

/** 速度 `speed`・半径 `radius` の旋回で、横 G を打ち消すバンク角 [rad]。 */
function bankFor(speed: number, radius: number): number {
  return Math.atan((speed * speed) / (radius * STANDARD_GRAVITY));
}

/** 傾き `degrees` から、上下 G `g` の曲線で傾きを `toDegrees` まで変えたときに上がる高さ（下がれば負）。 */
function riseOf(
  speed: number,
  g: number,
  degrees: number,
  toDegrees: number,
  ease?: number,
): number {
  return new TrackBuilder([0, 0, 0], 4, degrees).pitchByG(speed, g, toDegrees, {
    ...(ease === undefined ? {} : { ease }),
  }).height;
}

/**
 * いまの傾き `fromDegrees`（下り）のまま進み、上下 G `g` で傾きを `toDegrees` まで変えて、高さ `bottom` で
 * 引き起こしを終える。引き起こしを始める高さ（そこでの速度で曲線の大きさが決まる）を、繰り返して求める。
 * 速度は、高さ `refHeight` で速度 `refSpeed` からのエネルギー保存の見積もり。
 */
function descendTo(
  builder: TrackBuilder,
  fromDegrees: number,
  toDegrees: number,
  bottom: number,
  g: number,
  refSpeed: number,
  refHeight: number,
  ease?: number,
): TrackBuilder {
  // 引き起こしを始める高さは、いまの高さより下（振動しないよう、半分ずつ近づける）
  const highest = Math.max(bottom, builder.height - bottom);
  let pullDrop = 0;
  for (let i = 0; i < 40; i += 1) {
    const startHeight = Math.min(bottom + pullDrop, bottom + highest);
    const start = speedAt(refSpeed, refHeight, startHeight, 1);
    const next = -riseOf(start, g, fromDegrees, toDegrees, ease);
    pullDrop = Math.min(highest, (pullDrop + next) / 2);
  }
  const fall =
    (builder.height - bottom - pullDrop) / Math.abs(Math.sin((fromDegrees * Math.PI) / 180));
  builder.straight(Math.max(0, fall));
  return builder.pitchByG(speedAt(refSpeed, refHeight, builder.height, 1), g, toDegrees, {
    ...(ease === undefined ? {} : { ease }),
  });
}

/**
 * 丘（トップハット・キャメルバック）：上下 G `pullG` で傾き `degrees` まで引き起こして上り、頂上（高さ
 * `top`）を浮く G `crestG` で越え、同じ傾きで下って、もとの高さで水平に戻る。進入速度 `speed`。
 * G の上限の中で、頂上が高さ `top` になるよう、傾きを `degrees` から下げて選ぶ（速いと立ち上げられない）。
 */
function hill(
  builder: TrackBuilder,
  speed: number,
  top: number,
  degrees: number,
  pullG: number,
  crestG: number,
): TrackBuilder {
  const base = builder.height;
  let angle = degrees;
  let climb = 0;
  for (; angle > 10; angle -= 1) {
    const pull = riseOf(speed, pullG, 0, angle);
    const crest = riseOf(speedAt(speed, base, base + pull, 1), crestG, angle, 0);
    climb = top - base - pull - crest;
    if (climb >= 0) break;
  }
  builder.pitchByG(speed, pullG, angle).straight(climb / Math.sin((angle * Math.PI) / 180));
  builder.pitchByG(speedAt(speed, base, builder.height, 1), crestG, -angle);
  return descendTo(builder, -angle, 0, base, pullG, speed, base);
}

/** 高さ 約 `top` のトップハット（急な上り下り、頂上で強く浮く）。進入速度 `speed`。 */
function topHat(builder: TrackBuilder, speed: number, top: number): TrackBuilder {
  return hill(builder, speed, top, 85, PULL_G, CREST_G);
}

/** 高さ `height` だけ上がるキャメルバック（頂上は浮く G `crestG`）。進入速度 `speed`。 */
function camelback(
  builder: TrackBuilder,
  speed: number,
  height: number,
  degrees = 35,
  crestG = 0,
): TrackBuilder {
  return hill(builder, speed, builder.height + height, degrees, 3, crestG);
}

/**
 * 最後のブレーキ：速度 `speed`（見積もり。多めに）から、減速のブレーキで 10 m/s まで落とし、
 * 停止ブレーキで止める。長さは、減速度から求めた距離に余裕を足す。
 */
function brakeRun(builder: TrackBuilder, speed: number): TrackBuilder {
  const trimSpeed = 10;
  const trimLength = Math.max(
    20,
    (speed * speed - trimSpeed * trimSpeed) / (2 * BRAKE_DECELERATION),
  );
  return builder
    .straight(trimLength * 1.2 + 10, {
      segment: 'brake',
      params: { targetSpeed: trimSpeed, maxDeceleration: BRAKE_DECELERATION },
    })
    .straight(40, { segment: 'brake', params: { targetSpeed: 0, maxDeceleration: 5 } });
}

/** チェーンリフト：駅 → 傾き `degrees` で、頂上の高さ `top` まで巻き上げ → 頂上で水平に。 */
function stationAndLift(top: number, degrees: number, spacing = 4): TrackBuilder {
  const builder = new TrackBuilder([0, 2, 0], spacing)
    .straight(16, { segment: 'station' })
    .pitchTo(20, degrees, { segment: 'lift' });
  const flatten = new TrackBuilder([0, 0, 0], 4, degrees).pitchTo(20, 0).height;
  const rise = top - builder.height - flatten;
  return builder
    .straight(rise / Math.sin((degrees * Math.PI) / 180), { segment: 'lift' })
    .pitchTo(20, 0, { segment: 'lift' });
}

/** リフトの頂上（ゆっくり）から、傾き −`degrees` の落下に入り、高さ `bottom` で水平に戻る。 */
function firstDrop(
  builder: TrackBuilder,
  degrees: number,
  bottom: number,
  pullG = PULL_G,
): TrackBuilder {
  const top = builder.height;
  const spacing = builder.currentSpacing;
  // 頂上は、浮く G（{@link CREST_G}）を保つ曲線（ゆっくりのところは半径 6 m より急にしない。
  // 出口で G を戻さず、そのまま引き起こしへつなぐ）
  builder
    .withSpacing(1)
    .straight(4)
    .pitchByG(3, CREST_G, -degrees, { minRadius: 6, ease: 10, easeOut: 0 })
    .withSpacing(spacing);
  return descendTo(builder, -degrees, 0, bottom, pullG, 3, top);
}

/**
 * ドドンパ風（富士急ハイランド、2001 年）：圧縮空気の発進で、1.8 秒で 172 km/h（47.8 m/s）まで加速 →
 * 高さ 約 52 m のタワー（トップハット）→ 大きなバンク付きの旋回 → 停止ブレーキ。
 */
export function dodonpaPoints(): BuiltPoint[] {
  const v = 47.8;
  const b = new TrackBuilder([0, 2, 0], 4)
    .straight(14, { segment: 'station' })
    .straight(50, launch(v, 26.5))
    .straight(40);
  topHat(b, v, 48);
  const after = speedAt(v, 2, b.height, 0.94);
  b.straight(30)
    .turn(60, 180, { bank: bankFor(after, 60), transition: 35 })
    .straight(30);
  camelback(b, after, 10, 15, 0.4);
  return brakeRun(b.straight(20), after).build();
}

/**
 * ド・ドドンパ風（富士急ハイランド、2017 年）：圧縮空気の発進で、1.56 秒で 180 km/h（50 m/s）まで加速 →
 * 大きな垂直ループ → 大きなバンク付きの旋回 → 停止ブレーキ。
 */
export function doDodonpaPoints(): BuiltPoint[] {
  const v = 50;
  const b = new TrackBuilder([0, 2, 0], 4)
    .straight(14, { segment: 'station' })
    .straight(50, launch(v, 32))
    .straight(60)
    .loop(v * 0.95, 4.4, 4, 4);
  const after = speedAt(v, 2, b.height, 0.94);
  b.straight(40)
    .turn(70, 180, { bank: bankFor(after, 70), transition: 40 })
    .straight(30);
  camelback(b, after, 10, 15, 0.4);
  return brakeRun(b.straight(20), after).build();
}

/**
 * FUJIYAMA 風（富士急ハイランド、1996 年）：高さ 約 79 m まで巻き上げ、約 70 m を落下（最高 約 130 km/h）→
 * 大きなキャメルバックを越えて折り返し → 戻りの丘とヘリックス → 停止ブレーキ。全長 約 2 km。
 */
export function fujiyamaPoints(): BuiltPoint[] {
  const b = stationAndLift(79, 35);
  firstDrop(b, 65, 9);
  let v = speedAt(3, 79, b.height, 0.95);
  b.straight(20);
  camelback(b, v, 55, 40, 0);
  v = speedAt(3, 79, b.height, 0.92);
  b.straight(20);
  camelback(b, v, 38, 35, 0.1);
  v = speedAt(3, 79, b.height, 0.9);
  // 折り返し：大きなバンク付きの旋回
  b.straight(20)
    .turn(45, 180, { bank: bankFor(v * 0.95, 45), transition: 30 })
    .straight(20);
  camelback(b, v * 0.95, 26, 30, 0.2);
  v = speedAt(3, 79, b.height, 0.86);
  b.straight(20);
  camelback(b, v, 16, 25, 0.3);
  v = speedAt(3, 79, b.height, 0.84);
  // 終盤のヘリックス（1 周）
  b.straight(20)
    .turn(28, 360, { bank: bankFor(v * 0.9, 28), transition: 20 })
    .straight(20);
  return brakeRun(b, v).build();
}

/**
 * ええじゃないか風（富士急ハイランド、2006 年）：高さ 約 76 m まで巻き上げ、ほぼ垂直（88°）の落下
 * （最高 約 126 km/h）→ 2 つの垂直ループ → バンク付きの旋回 → 停止ブレーキ。
 * 実物は座席が前後に回転する「4 次元」コースター。このアプリでは座席の回転は表せない（軌道だけを真似る）。
 */
export function eejanaikaPoints(): BuiltPoint[] {
  const b = stationAndLift(76, 45, 2);
  firstDrop(b, 88, 8, 3.9);
  let v = speedAt(3, 76, b.height, 0.95);
  // ループは、抵抗で遅くなる分を見込んで小さめに作る（実際の G は、設計より 1〜2 割高い）
  b.straight(20).loop(v * 0.92, 3.8, 2, 2);
  v = speedAt(3, 76, b.height, 0.85);
  b.straight(20)
    .turn(45, 180, { bank: bankFor(v, 45), transition: 30 })
    .straight(20);
  b.loop(v * 0.92, 3.8, 2, 2);
  v = speedAt(3, 76, b.height, 0.8);
  b.straight(20)
    .turn(50, -180, { bank: bankFor(v, 50), transition: 30 })
    .straight(20);
  return brakeRun(b, v).build();
}

/**
 * 高飛車風（富士急ハイランド、2011 年）：暗い区間をリニアモーターで発進（約 70 km/h）→ 垂直ループ →
 * 旋回 → 高さ 約 43 m まで垂直に巻き上げ → 頂上で向きを変え、121° の「垂直を超える」落下
 * （最高 約 100 km/h）→ 停止ブレーキ。
 */
export function takabishaPoints(): BuiltPoint[] {
  const b = new TrackBuilder([0, 2, 0], 3)
    .straight(14, { segment: 'station' })
    .straight(40, launch(19.5, 6))
    .straight(15)
    .loop(19, 3.5, 1.5, 0.8)
    .straight(15)
    .turn(24, 180, { bank: bankFor(17, 24), transition: 16 })
    .straight(20)
    // 垂直の巻き上げ
    .pitchTo(10, 90, { segment: 'lift' });
  b.straight(44 - b.height - 8, { segment: 'lift' })
    .pitchTo(8, 0, { segment: 'lift' })
    // 頂上の平らな区間（落下で後ろへ戻る分が、巻き上げの塔に届かない長さ）
    .straight(16, { segment: 'lift' });
  const top = b.height;
  b.withSpacing(1);
  // 121° の落下：垂直を超えて、来た向きへ戻りながら落ちる。遅いうちに大きく傾け、速くなるほど緩める
  // （強く浮く G（約 −1 G）を保つ曲線。頂上のゆっくりのところは、半径 4 m より急にしない。
  // 出口で G を戻さず、そのまま引き起こしへつなぐ。垂直を超えた向きのまま長く落ちないようにする）
  b.pitchByG(3, -1.1, -121, { minRadius: 3.5, ease: 0 });
  // 落下の後は、乗る人の頭の向きへ引き起こす（傾き −121° → 0°。頂上の下をくぐって、もとの向きへ出る）
  descendTo(b, -121, 0, 4, 3.9, 3, top, 5);
  b.withSpacing(3);
  const v = speedAt(3, top, b.height, 0.95);
  return brakeRun(b.straight(20), v).build();
}

/**
 * フォーミュラ・ロッサ風（フェラーリ・ワールド・アブダビ、2010 年）：油圧の発進で、4.9 秒で 240 km/h
 * （66.7 m/s）まで加速 → 高さ 約 52 m のトップハット → 高速のバンク付きの S 字カーブ → 停止ブレーキ。
 * 全長 約 2 km。
 */
export function formulaRossaPoints(): BuiltPoint[] {
  const v = 66.7;
  const b = new TrackBuilder([0, 2, 0], 5)
    .straight(14, { segment: 'station' })
    .straight(180, launch(v, 13.6))
    .straight(60);
  topHat(b, v, 48);
  let after = speedAt(v, 2, b.height, 0.95);
  b.straight(60)
    .turn(160, 120, { bank: bankFor(after, 160), transition: 80 })
    .straight(60);
  after *= 0.97;
  b.turn(140, -150, { bank: bankFor(after, 140), transition: 70 }).straight(60);
  camelback(b, after * 0.97, 22, 20, 0.2);
  after *= 0.92;
  b.straight(40)
    .turn(110, 160, { bank: bankFor(after, 110), transition: 60 })
    .straight(40);
  return brakeRun(b, after).build();
}
